/**
 * Core type definitions for AnyPlay
 */

export enum UserRole {
  SUPERADMIN = "SUPERADMIN",
  ADMIN = "ADMIN",
  USER = "USER",
  GUEST = "GUEST",
}

/** How AnyPlay reaches a MediaPlex appliance to discover its outputs. */
export type ApplianceReadMode = "direct" | "iot";

/**
 * Optional MediaPlex appliance association on a Business Unit (mirror of the
 * backend BusinessUnitAppliance). No raw secret is ever stored here — "direct"
 * mode keeps only a Secrets Manager reference to the read-scoped mpk_ key.
 */
export interface BusinessUnitAppliance {
  /** Stable id for this appliance entry (server-generated). */
  id: string;
  mode: ApplianceReadMode;
  label?: string;
  /** Optional client scope: when set, this appliance serves only that client. */
  clientId?: string;
  // Mode B ("direct")
  baseUrl?: string;
  apiKeySecretRef?: string;
  // Mode A ("iot")
  applianceId?: string;
  addedBy?: string;
  addedAt?: string;
}

export interface BusinessUnit {
  id: string;
  name: string;
  description?: string;
  color?: string;
  logoUrl?: string;
  /** MediaPlex appliance associations (0..N; each optionally client-scoped). */
  appliances?: BusinessUnitAppliance[];
  createdAt: string;
  updatedAt: string;
}

/**
 * A live element candidate returned by GET /api/business-units/:id/appliance/elements
 * (mirror of the backend ApplianceElementCandidate). Ready to append as an event
 * element (name/url/type/aspectRatio); streamId/protocol are informational.
 *
 * Secrecy: for non-@nepgroup.com callers the server redacts `name` to a generic
 * "<PROTO> stream <n>" label, `url` to origin-only, and blanks `streamId` — so
 * the leaky stream id/port ("test-h265-5003") never reaches those callers.
 */
export interface ApplianceElementCandidate {
  name: string;
  url: string;
  type: "SLDP" | "Frame" | "WHEP" | "WebRTC" | "OvenMedia" | "HLS" | "Image" | "Blank";
  aspectRatio: string;
  streamId: string;
  protocol: "udp" | "rtp" | "rist" | "srt" | "omt" | "omt_video" | "whep" | "llhls";
  /** Id of the appliance entry this candidate came from. */
  applianceId: string;
  /** Client scope of the source appliance, if it is client-scoped. */
  clientId?: string;
}

/** Per-appliance status in a multi-appliance element listing (mirror of backend). */
export interface ApplianceStatus {
  applianceId: string;
  label?: string;
  clientId?: string;
  mode: ApplianceReadMode;
  candidateCount: number;
  streamCount: number;
  error?: string;
  errorKind?: "unsupported" | "offline" | "config";
}

export interface UserData {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: UserRole;
  businessUnitId?: string;
  businessUnitName?: string;
  buRole?: "admin" | "superuser" | "member";
  assignedStreamIds?: string[];
  assignedClientIds?: string[];
  assignedEventIds?: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface StreamConfig {
  streamId: string;
  name: string;
  url: string;
  protocol: "sldp" | "webrtc" | "whep" | "ovenmedia";
  businessUnitId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface GuestInvite {
  inviteId: string;
  email: string;
  businessUnitId: string;
  streamIds: string[];
  expiresAt?: number; // Unix timestamp (TTL)
  status: "pending" | "accepted" | "revoked";
  createdBy: string;
  createdAt: string;
}
